import * as React from 'react';
import { format } from 'date-fns';
import {
  DayPicker,
  type DayButtonProps,
  type ChevronProps,
} from 'react-day-picker';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
} from 'lucide-react';

import { buttonVariants } from './Button';
import { cn } from '../../lib/utils';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function CalendarDayButton({ className, day, ...props }: DayButtonProps) {
  return (
    <button
      className={cn(className, 'day-selected')}
      data-day={format(day.date, 'yyyy-MM-dd')}
      {...props}
    />
  );
}

function CalendarChevron({
  className,
  orientation = 'left',
  ...props
}: ChevronProps) {
  const Icon = {
    down: ChevronDownIcon,
    left: ChevronLeftIcon,
    right: ChevronRightIcon,
    up: ChevronUpIcon,
  }[orientation];

  return <Icon className={cn('h-4 w-4', className)} {...props} />;
}

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  const isDropdownCaption = props.captionLayout === 'dropdown';

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        root: 'w-fit',
        months: 'flex flex-col gap-4',
        month: 'space-y-4',
        month_caption: 'relative flex items-center justify-center gap-2 pt-1',
        caption_label: isDropdownCaption ? 'sr-only' : 'text-sm font-medium',
        nav: 'flex items-center gap-1',
        button_previous: cn(
          buttonVariants({ variant: 'ghost', size: 's', color: 'secondary' }),
          'h-8 w-8 p-0 text-contrast hover:text-contrast',
        ),
        button_next: cn(
          buttonVariants({ variant: 'ghost', size: 's', color: 'secondary' }),
          'h-8 w-8 p-0 text-contrast hover:text-contrast',
        ),
        month_grid: 'w-full border-collapse',
        weekdays: 'flex',
        weekday:
          'w-9 rounded-md text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground',
        week: 'mt-2 flex w-full',
        day: 'h-9 w-9 p-0 text-center text-sm',
        day_button:
          'inline-flex h-9 w-9 items-center justify-center rounded-md p-0 font-sans text-sm font-semibold text-white transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none',
        selected:
          'rounded-md bg-accent text-accent-foreground [&>button]:bg-accent [&>button]:text-accent-foreground [&>button]:hover:bg-accent/90',
        today: 'rounded-md border border-accent/50 [&>button]:text-accent',
        outside: '[&>button]:text-white/55',
        disabled: '[&>button]:cursor-not-allowed [&>button]:text-white/42',
        hidden: 'invisible',
        dropdowns: 'flex items-center gap-2',
        dropdown_root:
          'relative rounded-md border border-input bg-background text-contrast shadow-sm',
        dropdown:
          'h-8 rounded-md border-0 bg-transparent px-2 pr-8 text-sm text-contrast outline-none',
        chevron: 'h-4 w-4 fill-current',
        ...classNames,
      }}
      components={{ Chevron: CalendarChevron, DayButton: CalendarDayButton }}
      {...props}
    />
  );
}

Calendar.displayName = 'Calendar';

export { Calendar };
